import { useContext, useEffect, useState } from 'react'
import { ServerState, ViewItem, ViewItemFilter, createViewItems } from '../itemModel'
import { ItemsContext } from '../ItemsRoot'
import { groupItems } from '../grouping'
import VSpace from '../../common/VSpace'
import { Space, Switch, Table } from 'antd'
import { centsToDollarsDisplay } from '../../displayFormatters'
import { roundToPrecision } from '../../../helpers/math'
import Units from '../Units'
import { sum } from '../../../helpers/array'
import { VisitSelector } from '../VisitSelector'
import MultiSelect from '../../common/FormControls/MultiSelect'
import { WorkareaItemTypes } from '../../../common/constants'
import { hasValue } from '../../../helpers/nullable'

export type ItemSummaryFilter = {
  projectId: number
  workareaIds: number[]
  visitIds: string[]
  notReviewed: boolean
  reviewed: boolean
  invoiced: boolean
}

export type Props = {
  onFilterChange: (filter: ItemSummaryFilter) => void
  onHasItems: (hasItems: boolean) => void
}

export const ItemSummary = ({ onFilterChange, onHasItems }: Props) => {
  const { changeSet, jobItemsQuery, jobData } = useContext(ItemsContext)
  const workareaItems = jobItemsQuery.data?.actualItems ?? []
  const catalogItems = jobItemsQuery.data?.catalogItems ?? []
  const serverState: ServerState = {
    workareaItems,
    catalogItems,
    templateItems: [],
    estimatedQuantities: {},
  }

  const [workareaIds, setWorkareaIds] = useState<number[]>([])
  const [visitIds, setVisitIds] = useState<string[]>([])
  const [notReviewed, setNotReviewed] = useState(true)
  const [reviewed, setReviewed] = useState(true)
  const [invoiced, setInvoiced] = useState(true)

  useEffect(() => {
    setWorkareaIds(jobData.workareas.map(x => x.id))
  }, [jobData.workareas.length])

  useEffect(() => {
    setVisitIds(jobData.visitData.map(x => x.id))
  }, [jobData.visitData.length])

  useEffect(() => {
    onFilterChange({
      projectId: jobData.jobId,
      workareaIds: workareaIds,
      visitIds: visitIds,
      notReviewed,
      reviewed,
      invoiced
    })
  }, [jobData.jobId, workareaIds, visitIds, notReviewed, reviewed, invoiced])

  const filter: ViewItemFilter = {
    workareaItemType: WorkareaItemTypes.Actual
  }
  const viewItems = createViewItems(changeSet, serverState, jobData.visitData, jobData.budgets, jobData.jobIsSold, jobData.workareas, filter)

  const filterItem = (item: ViewItem): boolean => {
    if (!workareaIds.includes(item.workareaId)) {
      return false
    }

    if (hasValue(item.visit) && !visitIds.includes(item.visit?.id)) {
      return false
    }

    if (!notReviewed && !item.visit?.billed) {
      return false
    }

    if (!reviewed && hasValue(item.visit) && item.visit.billed) {
      return false
    }

    if (!invoiced && item.isInvoiced) {
      return false
    }

    return workareaIds.includes(item.workareaId)
      && (!hasValue(item.visit) || visitIds.includes(item.visit.id))
  }

  const itemGroups = groupItems([], viewItems.filter(x => !x.isFromTimesheets && filterItem(x)), catalogItems, jobData.workareas)
  const laborItems = viewItems.filter(x => x.isFromTimesheets && filterItem(x))

  const hasItems = itemGroups.length > 0 || laborItems.length > 0
  useEffect(() => {
    onHasItems(hasItems)
  }, [hasItems])

  return <VSpace size="large">
    <Space>
      <MultiSelect
        options={jobData.workareas.map(w => ({ value: w.id, label: w.name }))}
        style={{ width: 300 }}
        selectedValues={workareaIds}
        onChange={setWorkareaIds}
        maxTagCount={3}
        placeholder="Select Workareas"
        allSelectedText="All Workareas Selected"
      />
      <VisitSelector
        selectedVisitIds={visitIds}
        onChange={setVisitIds}
        viewItems={viewItems}
        visitData={jobData.visitData}
      />
    </Space>
    <Space size="large">
      <Space><Switch checked={notReviewed} onChange={setNotReviewed} />Not-Reviewed</Space>
      <Space><Switch checked={reviewed} onChange={setReviewed} />Reviewed</Space>
      <Space><Switch checked={invoiced} onChange={setInvoiced} />Invoiced</Space>
    </Space>
    <Table
      dataSource={itemGroups}
      rowKey="key"
      columns={[
        {
          dataIndex: 'name',
          title: 'Item',
        },
        {
          dataIndex: 'actualQuantity',
          title: 'Quantity',
          align: 'right',
          render: (_, item) => <Space>{roundToPrecision(item.actualQuantity, 2)}<Units units={item.units} /></Space>
        },
        {
          key: 'numberOfEntries',
          title: "# Entries",
          render: (_, item) => item.actualItems.length,
          align: 'right',
        },
        {
          dataIndex: 'actualTotalCost',
          title: 'Total Cost + Tax',
          align: 'right',
          render: centsToDollarsDisplay,
        },
        {
          dataIndex: 'totalActualPrice',
          title: 'Total Price',
          render: centsToDollarsDisplay,
          align: 'right',
        }
      ]}
      summary={() => <Table.Summary.Row style={{ fontWeight: 'bold' }}>
        <Table.Summary.Cell index={0} colSpan={3} align='left'>Total</Table.Summary.Cell>
        <Table.Summary.Cell index={1} align="right">{centsToDollarsDisplay(sum(itemGroups, x => x.actualTotalCost))}</Table.Summary.Cell>
        <Table.Summary.Cell index={2} align="right">{centsToDollarsDisplay(sum(itemGroups, x => x.totalActualPrice))}</Table.Summary.Cell>
      </Table.Summary.Row>}
      pagination={false}
    />
    <Table
      dataSource={laborItems}
      rowKey="guid"
      columns={[
        {
          key: 'visitData',
          title: 'Labor',
          render: (_, item) => {
            return <>
              { filterItem(item) ?
                `Timesheets Labor - ${
                  item.visit?.date 
                      ? new Date(item.visit.date).toLocaleDateString() 
                      : 'No date available'
              }`
                :
                `Timesheets Labor`
              }
            </>
          },
        },
        {
          key: 'technician',
          title: 'Technician',
          render: (_, item) => <>
          { filterItem(item) ?
            item.visit?.technicians ? item.visit.technicians : ''
          :
            ``
          }
          </>
        },
        {
          dataIndex: 'quantity',
          title: 'Hours',
          render: (_, item) => <Space>{roundToPrecision(item.quantity, 2)}<Units units={item.units} /></Space>,
          align: 'right',
        },
        {
          dataIndex: 'totalCost',
          title: 'Total Cost',
          render: centsToDollarsDisplay,
          align: 'right'
        },
        {
          dataIndex: 'totalPrice',
          title: 'Total Price',
          render: centsToDollarsDisplay,
          align: 'right'
        }
      ]}
      summary={() => <Table.Summary.Row style={{ fontWeight: 'bold' }}>
        <Table.Summary.Cell index={0} colSpan={2} align="left">Total</Table.Summary.Cell>
        <Table.Summary.Cell index={1} align="right">
          {laborItems.length > 0 &&
            <Space>
              {roundToPrecision(sum(laborItems, x => x.quantity), 2)}
              <Units units={laborItems[0].units} />
            </Space>
          }
        </Table.Summary.Cell>
        <Table.Summary.Cell index={2} align="right">
          {centsToDollarsDisplay(sum(laborItems, x => x.totalCost))}
        </Table.Summary.Cell>
        <Table.Summary.Cell index={3} align="right">
          {centsToDollarsDisplay(sum(laborItems, x => x.totalPrice))}
        </Table.Summary.Cell>
      </Table.Summary.Row>
      }
      pagination={false}
    />
  </VSpace>
}

export default ItemSummary
